/*
 * PvSelfConsumptionRenderer — PV production, household draw, grid, battery
 * in one glance. Hero shows current PV output; three horizontal bars stack
 * house-usage, grid feed-in/draw and battery charge/discharge with sign +
 * colour telling the operator direction at a glance. Battery SoC gets a
 * tiny vertical battery icon on the right. Daily totals become footer chips.
 */

import type { PvSelfConsumptionWidget } from '../widgets';
import type { IPointBindingSource } from '../binding';
import {
  usePoint,
  useOptionalPoint,
  numeric,
  quality,
  formatPower,
} from './_compound-helpers';
import { formatNumber } from '../../format/leafValue';
import './PvSelfConsumptionRenderer.css';

interface Props {
  widget: PvSelfConsumptionWidget;
  source: IPointBindingSource;
}

export function PvSelfConsumptionRenderer({ widget, source }: Props) {
  const pv = usePoint(source, widget.pvPowerPoint);
  const household = useOptionalPoint(source, widget.householdPowerPoint);
  const grid = useOptionalPoint(source, widget.gridPowerPoint);
  const batteryPower = useOptionalPoint(source, widget.batteryPowerPoint);
  const batterySoc = useOptionalPoint(source, widget.batterySocPoint);
  const production = useOptionalPoint(source, widget.dailyProductionEnergyPoint);
  const selfUse = useOptionalPoint(source, widget.dailySelfUseEnergyPoint);
  const feedIn = useOptionalPoint(source, widget.dailyFeedInEnergyPoint);
  const gridDraw = useOptionalPoint(source, widget.dailyGridDrawEnergyPoint);

  const pvVal = numeric(pv.value.value?.value);
  const houseVal = numeric(household?.value.value?.value);
  const gridVal = numeric(grid?.value.value?.value);
  const batteryVal = numeric(batteryPower?.value.value?.value);
  const socVal = numeric(batterySoc?.value.value?.value);
  const prodVal = numeric(production?.value.value?.value);
  const selfVal = numeric(selfUse?.value.value?.value);
  const feedInVal = numeric(feedIn?.value.value?.value);
  const gridDrawVal = numeric(gridDraw?.value.value?.value);

  const pvStale = quality(pv.value.value) !== 'ok';
  const houseStale = quality(household?.value.value ?? null) !== 'ok';
  const tileStale = pvStale && houseStale;

  const heroPower = formatPower(pvVal);

  // Scale for the horizontal bars: max magnitude among the three so bars are
  // visually comparable. Fall back to 1 kW to avoid /0.
  const scale = Math.max(
    Math.abs(houseVal ?? 0),
    Math.abs(gridVal ?? 0),
    Math.abs(batteryVal ?? 0),
    1000,
  );

  // Grid sign convention: positive = feed-in (export), negative = draw (import).
  const gridTone = gridVal == null ? 'neutral' : gridVal >= 0 ? 'feed' : 'draw';
  // Battery: positive = charge, negative = discharge.
  const batteryTone = batteryVal == null ? 'neutral' : batteryVal >= 0 ? 'charge' : 'discharge';

  return (
    <div class={`hv-pv${tileStale ? ' hv-pv--stale' : ''}`}>
      <header class="hv-pv__head">
        <div class="hv-pv__title">
          <div class="hv-pv__name">PV & Eigenverbrauch</div>
          {widget.label && <div class="hv-pv__label">{widget.label}</div>}
        </div>
      </header>

      <div class="hv-pv__body">
        <div class="hv-pv__hero">
          <div class="hv-pv__hero-value">
            {heroPower.text}<span>{heroPower.unit}</span>
          </div>
          <div class="hv-pv__hero-label">Erzeugung</div>
        </div>

        <div class="hv-pv__bars">
          <Bar label="Haus" value={houseVal} tone="consumption" scale={scale} />
          <Bar
            label={gridTone === 'draw' ? 'Netzbezug' : 'Einspeisung'}
            value={gridVal != null ? Math.abs(gridVal) : null}
            tone={gridTone === 'draw' ? 'grid-draw' : 'feed-in'}
            scale={scale}
          />
          {batteryVal != null && (
            <Bar
              label={batteryTone === 'charge' ? 'Batterie lädt' : 'Batterie liefert'}
              value={Math.abs(batteryVal)}
              tone={batteryTone === 'charge' ? 'battery-charge' : 'battery-discharge'}
              scale={scale}
            />
          )}
        </div>

        {batterySoc && <BatteryIcon soc={socVal} />}
      </div>

      <footer class="hv-pv__foot">
        {prodVal != null && (
          <span class="hv-pv__stat">
            <span class="hv-pv__stat-label">Produziert</span>
            <span class="hv-pv__stat-value">{formatNumber(prodVal, 1)} kWh</span>
          </span>
        )}
        {selfVal != null && (
          <span class="hv-pv__stat">
            <span class="hv-pv__stat-label">Selbst</span>
            <span class="hv-pv__stat-value">{formatNumber(selfVal, 1)} kWh</span>
          </span>
        )}
        {feedInVal != null && (
          <span class="hv-pv__stat">
            <span class="hv-pv__stat-label">Einspeisung</span>
            <span class="hv-pv__stat-value">{formatNumber(feedInVal, 1)} kWh</span>
          </span>
        )}
        {gridDrawVal != null && (
          <span class="hv-pv__stat">
            <span class="hv-pv__stat-label">Netz</span>
            <span class="hv-pv__stat-value">{formatNumber(gridDrawVal, 1)} kWh</span>
          </span>
        )}
      </footer>
    </div>
  );
}

// ---------- Horizontal bar ----------------------------------------------

type BarTone =
  | 'consumption'
  | 'feed-in'
  | 'grid-draw'
  | 'battery-charge'
  | 'battery-discharge';

function Bar({
  label,
  value,
  tone,
  scale,
}: {
  label: string;
  value: number | null;
  tone: BarTone;
  scale: number;
}) {
  const pct = value == null ? 0 : Math.min(100, (Math.abs(value) / scale) * 100);
  const text = value == null ? '—' : `${formatPower(value).text} ${formatPower(value).unit}`;
  return (
    <div class="hv-pv__bar">
      <div class="hv-pv__bar-header">
        <span class="hv-pv__bar-label">{label}</span>
        <span class="hv-pv__bar-value">{text}</span>
      </div>
      <div class="hv-pv__bar-track">
        <div class={`hv-pv__bar-fill hv-pv__bar-fill--${tone}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

// ---------- Battery icon -------------------------------------------------

function BatteryIcon({ soc }: { soc: number | null }) {
  const clamped = soc == null ? 0 : Math.max(0, Math.min(100, soc));
  const fillHeight = 60 * (clamped / 100);
  const y = 8 + (60 - fillHeight);
  const tone = clamped >= 60 ? 'ok' : clamped >= 25 ? 'warn' : 'low';
  return (
    <div class="hv-pv__battery" aria-label={`Ladezustand ${soc == null ? 'unbekannt' : Math.round(soc) + ' %'}`}>
      <svg width="32" height="76" viewBox="0 0 32 76" role="img">
        <rect x="10" y="0" width="12" height="6" rx="1.5" class="hv-pv__battery-cap" />
        <rect x="4" y="6" width="24" height="66" rx="4" class="hv-pv__battery-frame" />
        {soc != null && (
          <rect x="6" y={y} width="20" height={fillHeight} rx="2"
                class={`hv-pv__battery-fill hv-pv__battery-fill--${tone}`} />
        )}
      </svg>
      <div class="hv-pv__battery-soc">{soc == null ? '—' : `${Math.round(soc)} %`}</div>
    </div>
  );
}
